<template lang="pug">
div.pt-3.pb-8
  div.text-lg.font-medium.neutral700--text {{ $t('patient_information_form') }}
  div.text-sm.neutral700--text {{ $t('patient_information_form_desc') }}

  .form-fields
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
    template(v-else)
      v-card.round-xl.px-5.mt-8(outlined height="86")
        div.d-flex.align-center.fill-height
          v-icon(color="neutral500") $yzi-lock-03
          div.ml-4
            div.d-flex.align-center
              div.text-md.font-medium.neutral700--text {{ $t('name') }}
              v-chip.text-sm.warning700--text.ml-2(label small color="warning200") {{ $t('required') }}
            div.text-sm.neutral700--text.mt-1 Format: Text
      v-card.round-xl.px-5.mt-3(outlined height="86")
        div.d-flex.align-center.fill-height
          v-icon(color="neutral500") $yzi-lock-03
          div.ml-4
            div.d-flex.align-center
              div.text-md.font-medium.neutral700--text {{ $t('telephone_number') }}
              v-chip.text-sm.warning700--text.ml-2(label small color="warning200") {{ $t('required') }}
            div.text-sm.neutral700--text.mt-1 Format: Text

      draggable(handle=".handle" :list="fields" @start="drag = true" @end="endMove" :move="checkMove")
        template(v-for="field in fields")
          v-card.round-xl.px-5.mt-3(:key="field.idx" outlined height="86")
            div.d-flex.align-center.fill-height
              v-icon(v-if="field.idx < 3" color="neutral500") $yzi-lock-03
              div.handle(v-else :class="{ 'drag-active': drag, 'drag-inactive': !drag }")
                v-icon(color="neutral500") $yzi-dots-grid-02
              div.ml-4
                div.d-flex.align-center
                  div.text-md.font-medium.neutral700--text {{ field.field_name }}
                  v-chip.text-sm.warning700--text.ml-2(v-if="field.required" label small color="warning200") {{ $t('required') }}
                div.text-sm.neutral700--text.mt-1 Format: {{ getFormat(field.datatype) }}
              v-spacer
              div(v-if="field.idx || (!field.idx && !defaultIC)")
                y-switch.mb-n2(v-model="field.enabled" @change="enableField(field)")
              v-menu(v-if="field.idx || (!field.idx && !defaultIC)" offset-y left attach rounded="lg")
                template(#activator="{ on }")
                  v-btn.ml-4(icon small v-on="on")
                    v-icon mdi-dots-horizontal
                v-list(dense width="120")
                  template(v-if="field.idx < 3")
                    v-list-item(@click="requiredField(field)")
                      v-list-item-title.text-sm.basic800--text {{ field.required ? 'Not required' : 'Is required' }}
                  template(v-else)
                    v-list-item(@click="editField(field)")
                      v-list-item-title.text-sm.basic800--text {{ $t('edit') }}
                    v-divider
                    v-list-item(@click="deleteField(field)")
                      v-list-item-title.text-sm.danger500--text {{ $t('delete') }}

      v-card.round-xl.px-5.mt-3(link outlined height="86" @click="openFieldDialog = true")
        div.d-flex.align-center.fill-height
          v-avatar(color="neutral300" size="32")
            v-icon(color="neutral500" size="18") $yzi-plus
          div.text-md.font-semibold.neutral700--text.ml-4 {{ $t('add_field') }}

  .dialogs
    add-field-dialog(v-if="openFieldDialog" :open.sync="openFieldDialog" :field="selectedField" @updated="reset" @closed="selectedField = null")
    delete-field-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :field="selectedField" @deleted="reset")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddFieldDialog from '@/components/clinic/dialogs/AddFieldDialog.vue'
import DeleteFieldDialog from '@/components/clinic/dialogs/DeleteFieldDialog.vue'
import draggable from 'vuedraggable'

export default {
  name: 'ManagePatientForm',
  props: [],
  components: { AddFieldDialog, DeleteFieldDialog, draggable },
  data: () => ({
    isLoading: false,
    isFetching: false,
    openFieldDialog: false,
    openDeleteDialog: false,
    drag: false,
    selectedField: null,
    fields: [],
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic'
    }),
    defaultIC () {
      if (this.clinic && this.clinic.customer_uid_type === 'ic') {
        return true
      }
      return false
    }
  },
  mounted() {
    this.fetchPatientForm()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPatientForm () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/form_fields`)
        .then(resp => {
          this.fields = resp.data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    enableField (field) {
      this.$axios.patch(`/clinics/${this.company.slug}/form_fields/${field.idx}`, { enabled: field.enabled })
        .then(() => {
          this.showMessage(['success', this.$t('success_message.successfully_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    requiredField (field) {
      this.$axios.patch(`/clinics/${this.company.slug}/form_fields/${field.idx}`, { required: !field.required })
        .then(resp => {
          this.$set(field, 'required', resp.data.required)
          this.showMessage(['success', this.$t('success_message.successfully_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    editField (field) {
      this.selectedField = field
      this.openFieldDialog = true
    },
    deleteField (field) {
      this.selectedField = field
      this.openDeleteDialog = true
    },
    getFormat (datatype) {
      let dataTypes = [
        { text: 'Text', value: 'string' },
        { text: 'Email', value: 'email' },
        { text: 'Selection input', value: 'selection' },
        { text: 'Phone number', value: 'phone' },
        { text: 'Number only', value: 'number' },
        { text: 'Address', value: 'address' },
        { text: 'Text area field', value: 'text' }
      ]
      return dataTypes.find(f => f.value === datatype) ? dataTypes.find(f => f.value === datatype).text : ''
    },
    checkMove (e) {
      if (e.relatedContext.index < 3 || e.draggedContext.index < 3) {
        return false
      }
    },
    endMove (e) {
      this.drag = false
      const prevPosition = e.oldIndex
      const newPosition = e.newIndex
      if (!prevPosition || !newPosition) {
        return
      }
      if (newPosition === prevPosition) {
        return 
      }
      this.$axios.post(`/clinics/${this.company.slug}/form_fields/${prevPosition}/move/`, { position: newPosition }).catch(() => {})
    },
    reset () {
      this.selectedField = null
      this.fetchPatientForm()
    }
  }
}
</script>

<style scoped>
.v-sheet--outlined {
  border-color: var(--v-primary300);
}
.drag-inactive { cursor: grab; }
.drag-active { cursor: grabbing; }
</style>